function generateEasingFunction(inertia = 0) {
  return `cubic-bezier(0.42, ${inertia.toFixed(2)}, 0.58, 1.0)`;
}
class SkapaSmoothScroller extends EventTarget {
  constructor(target) {
    super();
    this.animationID = -1;
    this.isAnimating = false;
    this.targetScroll = 0;
    this.instant = false;
    this.isRTL = false;
    this.initHelpers = () => {
      const helper = document.createElement("skapa-carousel-animation-helper");
      helper.style.position = "absolute";
      helper.style.transition = "left ease-in-out 0ms";
      helper.style.transitionTimingFunction = generateEasingFunction();
      helper.style.visibility = "hidden";
      helper.ariaHidden = "true";
      this.target.parentNode.appendChild(helper);
      this.helper = helper;
    };
    this.debounceID = -1;
    /**
     * The function is used to keep the manual and external scrolling updates in sync.
     * The debounce feature is required to prevent the update from interfering with the scrolling.
     * @param val {number} sets the new scroll left value of the container
     */
    this.debouncedUpdate = (val) => {
      clearTimeout(this.debounceID);
      if (this.helper)
        this.debounceID = window.setTimeout(
          () => this.helper && (this.helper.style.left = `${val}px`),
          50
        );
    };
    /**
     * When using smooth scroll fallback mode, this function sets up and starts the animation of a proxy element
     * which is used to to calculate offset values on any given frame.
     * @returns nothing
     */
    this.startAnimation = () => {
      if (!this.helper)
        return;
      const helper = this.helper;
      helper.style.transitionDuration = "400ms";
      helper.style.left = `${this.targetScroll}px`;
      this.isAnimating = true;
      this.iterate();
    };
    // Applies the animation of the proxy helper element to the scroll container's scrollLeft property.
    this.iterate = () => {
      cancelAnimationFrame(this.animationID);
      if (!this.helper)
        return;
      const { isRTL } = this;
      const minimumScrollValue = isRTL ? -(this.target.scrollWidth - this.target.clientWidth) : 0;
      const maximumScrollValue = isRTL ? 0 : this.target.scrollWidth - this.target.clientWidth;
      if (this.targetScroll < minimumScrollValue)
        this.targetScroll = minimumScrollValue;
      else if (this.targetScroll > maximumScrollValue)
        this.targetScroll = maximumScrollValue;
      this.target.scrollLeft = this.helper.offsetLeft;
      if (Math.abs(this.targetScroll - this.target.scrollLeft) <= 1) {
        this.target.scrollLeft = this.targetScroll;
        this.onAnimationEnd();
      } else {
        this.helper.style.left = `${this.targetScroll}px`;
        this.animationID = requestAnimationFrame(this.iterate);
      }
    };
    // Stop animation
    this.onAnimationEnd = () => {
      const helper = this.helper;
      if (helper) {
        helper.style.transitionDuration = "0ms";
      }
      cancelAnimationFrame(this.animationID);
      this.isAnimating = false;
    };
    // Cleanup
    this.stopAnimations = () => {
      if (this.helper) {
        this.onAnimationEnd();
      }
    };
    this.target = target;
    this.initHelpers();
  }
  get scrollLeft() {
    return this.target.scrollLeft;
  }
  set scrollLeft(value) {
    this.targetScroll = value;
    if (this.instant && this.helper) {
      if (this.isAnimating)
        this.onAnimationEnd();
      this.helper.style.left = `${value}px`;
      this.target.scrollLeft = value;
    } else {
      this.startAnimation();
    }
  }
  // The scrollLeft value of the container once the animation has settled. If there are no animations then the current value is returned.
  get scrollTarget() {
    return this.targetScroll;
  }
  set inertia(value) {
    if (this.helper && !isNaN(value))
      this.helper.style.transitionTimingFunction = generateEasingFunction(
        Math.max(Math.min(value, 1), 0)
      );
  }
}
export {
  SkapaSmoothScroller
};
